import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Users, MapPin, Phone, Ticket, User, Clock,
  ChevronDown, ChevronUp, Anchor, Mail, Edit
} from "lucide-react";

/**
 * Reusable Booking Card Component
 * Used in: Schedule Tab, Bookings Calendar Day Modal
 * 
 * Props:
 * - booking: Booking data object
 * - slotColor: Left border color (optional)
 * - onEdit: Callback when Edit button clicked (optional)
 * - onContact: Callback for contact modal ({ type, value, name })
 * - onOpenTicket: Callback when Ticket button clicked (opens in same screen for iOS compatibility)
 * - showEditButton: Show the Edit button (default: false)
 */

// Format duration
const formatDuration = (minutes) => {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}hr`;
  return `${hours}hr ${mins}min`;
};

// Format time to 12-hour with AM/PM
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  
  // Handle various formats: "09:00", "09:00:00", "9:00 AM"
  // If already has AM/PM, return as-is
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }
  
  // Parse 24-hour format
  const [hourStr, minuteStr] = timeStr.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr ? minuteStr.substring(0, 2) : '00';
  
  if (isNaN(hour)) return timeStr;
  
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  
  return `${hour}:${minute} ${ampm}`;
};

const BookingCard = ({ 
  booking, 
  slotColor = '#3B82F6',
  onEdit,
  onContact,
  onOpenTicket,
  showEditButton = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const duration = formatDuration(booking.duration_minutes || booking.durationMinutes);
  
  // Build location string
  const locationParts = [booking.city, booking.country].filter(Boolean);
  const locationStr = locationParts.join(', ');
  
  // System fields to exclude from customer responses
  const SYSTEM_FIELDS = new Set([
    'currency', 'conversionRate', 'isMultiCurrency', 'presentmentCurrency',
    'selectedAddOns', 'customAnswers', 'referralCode', 'distributionRef'
  ]);
  
  const addOns = booking.add_ons || booking.selectedAddOns || [];
  const promoCode = booking.promo_code || booking.promoCode;
  const meaningfulResponses = (booking.custom_responses || booking.customAnswers || [])
    .filter(r => r && r.question && !SYSTEM_FIELDS.has(r.question) && 
                 r.answer !== null && r.answer !== undefined && r.answer !== '' && r.answer !== false);

  // Always show expand toggle — when empty it says "No additional info"
  // When populated shows add-ons, custom questions, promo codes
  const hasExpandableContent = true;  // always show so operators know state

  // Get team display text
  const getTeamDisplay = () => {
    if (booking.team_assignments?.length > 0) {
      const names = booking.team_assignments
        .filter(a => a.teamName)
        .map(a => a.teamName);
      return names.length > 0 ? names.join(', ') : 'Unassigned';
    }
    // Also check teamAssignments (camelCase from API)
    if (booking.teamAssignments?.length > 0) {
      const names = booking.teamAssignments
        .filter(a => a.teamName)
        .map(a => a.teamName);
      return names.length > 0 ? names.join(', ') : 'Unassigned';
    }
    return booking.team_member_name || booking.teamMemberName || 'Unassigned';
  };

  const hasTeamAssigned = 
    (booking.team_assignments?.length > 0 && booking.team_assignments.some(a => a.teamName)) ||
    (booking.teamAssignments?.length > 0 && booking.teamAssignments.some(a => a.teamName)) ||
    booking.team_member_name || booking.teamMemberName;

  // Handle ticket click - navigate in same screen (iOS compatible)
  const handleTicketClick = () => {
    const ticketUrl = booking.ticket_url || booking.ticketUrl;
    if (ticketUrl) {
      // Extract the path from the URL and navigate within app
      try {
        const url = new URL(ticketUrl);
        navigate(url.pathname + url.search);
      } catch {
        // If URL parsing fails, use as-is or call callback
        if (onOpenTicket) {
          onOpenTicket(ticketUrl);
        }
      }
    }
  };

  // Get asset name from either snake_case or camelCase
  const getAssetName = () => {
    return booking.asset_name || booking.assetName || null;
  };

  return (
    <div
      data-testid={`booking-card-${booking.confirmation_code || booking.confirmationCode}`}
      className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-shadow"
      style={{ borderLeft: `4px solid ${slotColor}` }}
    >
      <div className="p-4 sm:p-5 space-y-2.5">
        {/* ROW 1: Time pill + timezone abbr + Service name | Action buttons */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-sm font-semibold whitespace-nowrap">
                {formatTime12Hour(booking.start_time || booking.startTime)}
                {booking.timezone && (
                  <span className="ml-1 font-normal opacity-70 text-xs">
                    {booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'Chicago' ? 'CST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'New_York' ? 'EST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'Los_Angeles' ? 'PST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'Denver' ? 'MST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ')?.slice(0, 3).toUpperCase()}
                  </span>
                )}
              </span>
              <h3 className="font-semibold text-gray-900 truncate">
                {booking.service_name || booking.serviceName}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(booking.ticket_url || booking.ticketUrl) && (
              <Button variant="ghost" size="sm" onClick={handleTicketClick}
                className="h-8 gap-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 px-2.5">
                <Ticket className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Ticket</span>
              </Button>
            )}
            {showEditButton && onEdit && (
              <Button variant="ghost" size="sm" onClick={() => onEdit(booking)}
                className="h-8 gap-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 px-2.5">
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Edit</span>
              </Button>
            )}
          </div>
        </div>

        {/* ROW 2: Customer name | Payment status (right) + contact icons */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <User className="w-3 h-3 text-gray-500" />
            </div>
            <span className="font-medium text-gray-900 truncate text-sm">
              {booking.customer_name || booking.customerName}
            </span>
            {/* Payment — subtle text, not a badge */}
            {(booking.payment_status || booking.paymentStatus) && (
              <span className={`text-xs font-normal flex-shrink-0 ${
                (booking.payment_status || booking.paymentStatus) === 'paid' ? 'text-green-600' :
                (booking.payment_status || booking.paymentStatus) === 'deposit_paid' ? 'text-amber-600 font-semibold' :
                (booking.payment_status || booking.paymentStatus) === 'partial' ? 'text-amber-600' :
                (booking.payment_status || booking.paymentStatus) === 'refunded' ? 'text-gray-400' :
                'text-red-500'
              }`}>
                · {(booking.payment_status || booking.paymentStatus) === 'paid' ? 'Paid' :
                   (booking.payment_status || booking.paymentStatus) === 'deposit_paid' ? 'Security Deposit' :
                   (booking.payment_status || booking.paymentStatus) === 'partial' ? 'Part paid' :
                   (booking.payment_status || booking.paymentStatus) === 'refunded' ? 'Refunded' : 'Unpaid'}
              </span>
            )}
            {(booking.status === 'cancelled' || booking.status === 'checked_in') && (
              <span className={`text-xs flex-shrink-0 ${
                booking.status === 'cancelled' ? 'text-red-500' : 'text-green-600'
              }`}>
                · {booking.status === 'cancelled' ? 'Cancelled' : 'Checked In'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {(booking.customer_phone || booking.customerPhone) && onContact && (
              <button onClick={() => onContact({ type: 'phone', value: booking.customer_phone || booking.customerPhone, name: booking.customer_name || booking.customerName, booking })}
                className="w-7 h-7 rounded-full bg-gray-100 hover:bg-green-50 hover:text-green-600 flex items-center justify-center text-gray-400 transition-colors">
                <Phone className="w-3.5 h-3.5" />
              </button>
            )}
            {(booking.customer_email || booking.customerEmail) && onContact && (
              <button onClick={() => onContact({ type: 'email', value: booking.customer_email || booking.customerEmail, name: booking.customer_name || booking.customerName, booking })}
                className="w-7 h-7 rounded-full bg-gray-100 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-center text-gray-400 transition-colors">
                <Mail className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ROW 3: Location */}
        {(locationStr || booking.meeting_point || booking.pickupLocation) && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500">
            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
            <span>
              {locationStr && <span className="text-gray-700">{locationStr}</span>}
              {(booking.meeting_point || booking.pickupLocation) && (
                <span className="text-gray-400"> · {booking.meeting_point || booking.pickupLocation}</span>
              )}
            </span>
          </div>
        )}

        {/* ── Security Deposit: paid / owes row ── */}
        {(booking.payment_status === 'deposit_paid' || booking.paymentStatus === 'deposit_paid') &&
          (booking.deposit_paid > 0 || booking.remaining_balance > 0) && (
          <div className="flex items-center gap-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs">
            <span className="text-green-700 font-medium">
              Paid ${Number(booking.deposit_paid || booking.paid_amount || 0).toFixed(2)}
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-amber-700 font-semibold">
              Owes ${Number(booking.remaining_balance || 0).toFixed(2)}
            </span>
            <span className="ml-auto text-amber-600 text-xs">Due at check-in</span>
          </div>
        )}

        {/* ROW 4: Asset + Team — show asset when set, show team when asset is set */}
        {(getAssetName() || hasTeamAssigned) && (
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {getAssetName() && (
              <div className="flex items-center gap-1.5">
                <Anchor className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700">{getAssetName()}</span>
              </div>
            )}
            {/* Show team row when asset is set (may have requirements) OR team is assigned */}
            {(getAssetName() || hasTeamAssigned) && (
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span className={hasTeamAssigned ? 'text-gray-700' : 'text-gray-400'}>
                  {hasTeamAssigned ? getTeamDisplay() : 'Unassigned'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* FOOTER: Ref · Duration · Guests — small, muted */}
        <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-100">
          <div>
            {/* See more / expand toggle — always visible so operators know booking state */}
            {hasExpandableContent && (
              <button
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                className="flex items-center gap-1 text-xs text-blue-500 font-medium hover:text-blue-700 transition-colors"
              >
                {isExpanded ? 'Less' : 'More info'}
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
            <span>{booking.confirmation_code || booking.confirmationCode}</span>
            {duration && <><span>·</span><span>{duration}</span></>}
            <span>·</span>
            <span>{booking.party_size || booking.partySize || 1}p</span>
          </div>
        </div>
      </div>
      
      {/* EXPANDED SECTION */}
      {isExpanded && (
        <div className="px-4 sm:px-5 py-4 bg-gray-50 space-y-4 border-t border-gray-100">
          {/* Add-ons */}
          {addOns.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Add-ons</p>
              <div className="flex flex-wrap gap-1.5">
                {addOns.map((addon, idx) => {
                  const name  = addon?.title || addon?.name || addon?.addOnName || (typeof addon === 'string' ? addon : '—');
                  const price = addon?.price != null ? ` · $${addon.price}` : '';
                  const qty   = addon?.quantity > 1 ? `${addon.quantity}× ` : '';
                  return (
                    <span key={idx} className="text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 text-gray-700">
                      {qty}{name}{price}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No add-ons</p>
          )}

          {/* Promo code */}
          {promoCode && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 uppercase tracking-wide">Promo</span>
              <span className="text-xs font-mono bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-700">{promoCode}</span>
            </div>
          )}

          {/* Customer Q&A */}
          {meaningfulResponses.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Customer Responses</p>
              <div className="space-y-1.5">
                {meaningfulResponses.map((resp, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-xs">
                    <span className="text-gray-400 flex-shrink-0 min-w-[90px] truncate">{resp.question}</span>
                    <span className="text-gray-800 font-medium">{String(resp.answer)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No custom responses</p>
          )}

          {/* Notes */}
          {(booking.customer_notes || booking.customerNotes || booking.internal_notes || booking.internalNotes) && (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Notes</p>
              {(booking.customer_notes || booking.customerNotes) && (
                <p className="text-xs text-gray-600">{booking.customer_notes || booking.customerNotes}</p>
              )}
              {(booking.internal_notes || booking.internalNotes) && (
                <p className="text-xs text-gray-500 italic mt-0.5">Internal: {booking.internal_notes || booking.internalNotes}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingCard;
